/**
 * Site KPI — Delivery sub-tab: OTIF / Appeals / Spend cards and the six
 * delivery charts.
 *
 * CR-5868: split out of SiteKPITab.tsx (500-line limit). CR-8674: Depth of
 * Delivery is by month on both portals, so it no longer needs the supplier
 * portal's "by customer site" title.
 */
import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card'
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ComposedChart,
} from 'recharts'
import { useFormatters, usePreferences } from '../../../contexts/PreferencesContext'
import { useAxisAmount } from '../charts/useAxisAmount'
import { ChartWrapper } from '../ChartWrapper'
import { OTIFComparisonCard, SpendComparisonCard } from '../SiteKPIComponents'
import { OTIFChartTooltip } from './OTIFChartTooltip'
import { otifBarClick } from '../otifSegmentDrill'
import type { SiteKPIMetrics } from './siteKPIMetrics'
import type { SiteKPIDrilldowns } from './useSiteKPIDrilldowns'

interface SiteKPIDeliveryPanelProps {
  metrics: SiteKPIMetrics
  drill: SiteKPIDrilldowns
  showOTIF: boolean
  showOTD: boolean
}

export function SiteKPIDeliveryPanel({ metrics, drill, showOTIF, showOTD }: SiteKPIDeliveryPanelProps) {
  const { formatCurrency } = useFormatters()
  const axisAmount = useAxisAmount() // CR-7120: numbers only on graph axes
  const { preferences } = usePreferences()
  const { drilldownFilters, openDrilldown, handleChartClick } = drill
  const {
    otifThresholds, otifComparisonValues, spendComparisonValues, appealsData,
    otifDataWithTrend, otifDomain, appealsTrendData, spendTrendData,
    depthOfDeliveryData, arrearsByLinesData, arrearsByValueData,
  } = metrics

  // State for hover-to-highlight on OTIF chart legend (matches DefaultTab)
  const [hoveredOtifSeries, setHoveredOtifSeries] = useState<string | null>(null)

  const appealTile = (
    className: string, valueClass: string, label: React.ReactNode, value: number,
    filter: Record<string, string>, drillLabel: string,
  ) => (
    <div
      className={`flex flex-col items-center justify-center py-2 px-1 rounded-md cursor-pointer transition-all ${className}`}
      onClick={() => openDrilldown('appeals', { ...drilldownFilters, ...filter }, drillLabel)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDrilldown('appeals', { ...drilldownFilters, ...filter }, drillLabel); } }}
    >
      {/* CR-8674: every label takes two lines' height, so a one-line label
          (Accepted, Rejected) keeps its figure level with the others. */}
      <div className="min-h-[2.5em] flex items-center justify-center text-[9px] text-muted-foreground text-center leading-tight mb-1">
        <span>{label}</span>
      </div>
      <div className={`text-lg font-bold tabular-nums ${valueClass}`}>{value}</div>
    </div>
  )

  return (
    <div className="space-y-6">
      {/* KPI Metrics Row - OTIF, APPEALS, SPEND */}
      <div className={`grid gap-4 ${showOTIF ? 'grid-cols-3' : 'grid-cols-2'}`}>
        {/* OTIF Card - Side-by-side comparison of all periods */}
        {showOTIF && (
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">OTIF</CardTitle>
          </CardHeader>
          <CardContent>
            <OTIFComparisonCard values={otifComparisonValues} thresholds={otifThresholds} onSelect={drill.openOtifPeriod} />
          </CardContent>
        </Card>
        )}

        {/* APPEALS Card - CR-3836: Using CSS Grid for equal column distribution */}
        <Card className="shadow-sm overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">APPEALS</CardTitle>
          </CardHeader>
          <CardContent className="pt-2 px-4">
            <div className="grid grid-cols-5 gap-2">
              {appealTile('bg-blue-50 hover:bg-blue-100', 'text-blue-600', <>Raised by<br/>Customer</>, appealsData.raised_by_customer, { is_customer_appeal: 'true' }, 'Raised by Customer')}
              {appealTile('bg-purple-50 hover:bg-purple-100', 'text-purple-600', <>Raised by<br/>Supplier</>, appealsData.raised_by_supplier, { is_customer_appeal: 'false' }, 'Raised by Supplier')}
              {appealTile('bg-green-50 hover:bg-green-100', 'text-green-600', 'Accepted', appealsData.accepted, { status: 'accepted' }, 'Accepted Appeals')}
              {appealTile('bg-red-50 hover:bg-red-100', 'text-red-600', 'Rejected', appealsData.rejected, { status: 'rejected' }, 'Rejected Appeals')}
              {appealTile('bg-gray-50 hover:bg-gray-100', 'text-gray-600', <>Auto<br/>Approved</>, appealsData.auto_approved, { is_customer_appeal: 'true', status: 'accepted' }, 'Auto Approved Appeals')}
            </div>
          </CardContent>
        </Card>

        {/* SPEND Card - Side-by-side comparison of all periods */}
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">SPEND</CardTitle>
          </CardHeader>
          <CardContent>
            <SpendComparisonCard
              values={spendComparisonValues}
              formatCurrency={formatCurrency}
              onSelect={drill.openSpendPeriod}
            />
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1 - OTIF and Appeals */}
      <div className={`grid gap-4 ${showOTIF ? 'grid-cols-2' : 'grid-cols-1'}`}>
        {showOTIF && (
        <ChartWrapper
          title="OTIF GRAPH"
          data={otifDataWithTrend}
          kpiPercentDecimals={preferences.kpi_percent_decimals ?? 0}
          exportFilename="otif-graph"
          legend={[
            { color: '#1d4ed8', label: 'OTIF %', dataKey: 'otif' },
            { color: '#dc2626', label: 'Target', dataKey: 'target' },
            { color: '#f97316', label: 'Trend', dataKey: 'otifTrend' },
            { color: '#10b981', label: 'OTIF PO Lines', dataKey: 'otifCount' },
            { color: '#fbbf24', label: 'Arrears PO Lines', dataKey: 'arrears' },
            { color: '#ef4444', label: 'Failed OTIF PO Lines', dataKey: 'failed' },
          ]}
          hoveredSeries={hoveredOtifSeries}
          onSeriesHover={setHoveredOtifSeries}
        >
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart
              data={otifDataWithTrend}
              onClick={(chartData) => handleChartClick(chartData, 'otif')}
              style={{ cursor: 'pointer' }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} domain={otifDomain} allowDataOverflow={true} padding={{ top: 8 }} tickFormatter={(v) => `${v}%`} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} domain={[0, 'auto']} />
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} content={<OTIFChartTooltip />} />
              <Bar yAxisId="right" dataKey="otifCount" stackId="a" onClick={otifBarClick('positive', (m, seg) => handleChartClick({ activeLabel: m }, 'otif', undefined, seg))} fill="#10b981" fillOpacity={hoveredOtifSeries && hoveredOtifSeries !== 'otifCount' ? 0.2 : 1} name="OTIF PO Lines" />
              <Bar yAxisId="right" dataKey="arrears" stackId="a" onClick={otifBarClick('arrears', (m, seg) => handleChartClick({ activeLabel: m }, 'otif', undefined, seg))} fill="#fbbf24" fillOpacity={hoveredOtifSeries && hoveredOtifSeries !== 'arrears' ? 0.2 : 1} name="Arrears PO Lines" />
              <Bar yAxisId="right" dataKey="failed" stackId="a" onClick={otifBarClick('failed', (m, seg) => handleChartClick({ activeLabel: m }, 'otif', undefined, seg))} fill="#ef4444" fillOpacity={hoveredOtifSeries && hoveredOtifSeries !== 'failed' ? 0.2 : 1} name="Failed OTIF PO Lines" />
              <Line yAxisId="left" type="monotone" dataKey="otif" stroke="#1d4ed8" strokeWidth={3} strokeOpacity={hoveredOtifSeries && hoveredOtifSeries !== 'otif' ? 0.2 : 1} dot={{ r: 6, fill: '#1d4ed8', stroke: '#fff', strokeWidth: 2 }} name="OTIF %" />
              <Line yAxisId="left" type="monotone" dataKey="target" stroke="#dc2626" strokeWidth={3} strokeDasharray="8 4" strokeOpacity={hoveredOtifSeries && hoveredOtifSeries !== 'target' ? 0.2 : 1} dot={false} name="Target" />
              <Line yAxisId="left" type="monotone" dataKey="otifTrend" stroke="#f97316" strokeWidth={2} strokeDasharray="5 5" strokeOpacity={hoveredOtifSeries && hoveredOtifSeries !== 'otifTrend' ? 0.2 : 1} dot={false} name="Trend" />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartWrapper>
        )}

        <ChartWrapper
          title="APPEALS GRAPH"
          data={appealsTrendData}
          exportFilename="appeals-graph"
          legend={[
            { color: '#3b82f6', label: 'Awaiting Customer' },
            { color: '#10b981', label: 'Accepted' },
            { color: '#f59e0b', label: 'Awaiting Supplier' },
            { color: '#ef4444', label: 'Rejected' },
          ]}
        >
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={appealsTrendData}
              onClick={(chartData) => handleChartClick(chartData, 'appeals')}
              style={{ cursor: 'pointer' }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
              <Bar dataKey="awaitingCustomer" stackId="a" fill="#3b82f6" name="Awaiting Customer" />
              <Bar dataKey="accepted" stackId="a" fill="#10b981" name="Accepted" />
              <Bar dataKey="awaitingSupplier" stackId="a" fill="#f59e0b" name="Awaiting Supplier" />
              <Bar dataKey="rejected" stackId="a" fill="#ef4444" name="Rejected" />
            </BarChart>
          </ResponsiveContainer>
        </ChartWrapper>
      </div>

      {/* Charts Row 2 - Spend and Depth of Delivery */}
      <div className={`grid gap-4 ${showOTD ? 'grid-cols-2' : 'grid-cols-1'}`}>
        <ChartWrapper
          title="SPEND BY MONTH GRAPH"
          data={spendTrendData}
          exportFilename="spend-by-month"
        >
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart
              data={spendTrendData}
              onClick={(chartData) => handleChartClick(chartData, 'spend')}
              style={{ cursor: 'pointer' }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis
                tick={{ fontSize: 11 }}
                tickFormatter={axisAmount}
              />
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} formatter={(value: number) => formatCurrency(value)} />
              <Area
                type="monotone"
                dataKey="spend"
                stroke="#3b82f6"
                fill="#93c5fd"
                name="Spend"
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartWrapper>

        {showOTD && (
        <ChartWrapper
          title="DEPTH OF DELIVERY GRAPH"
          data={depthOfDeliveryData}
          exportFilename="depth-of-delivery"
          legend={[
            { color: '#10b981', label: 'On Time' },
            { color: '#fbbf24', label: 'Early' },
            { color: '#ef4444', label: 'Late' },
          ]}
        >
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={depthOfDeliveryData}
              onClick={(chartData) => handleChartClick(chartData, 'otd')}
              style={{ cursor: 'pointer' }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              {/* CR-8674: one bar per month; the click drills that month. */}
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
              <Bar dataKey="onTime" stackId="a" fill="#10b981" name="On Time" />
              <Bar dataKey="early" stackId="a" fill="#fbbf24" name="Early" />
              <Bar dataKey="late" stackId="a" fill="#ef4444" name="Late" />
            </BarChart>
          </ResponsiveContainer>
        </ChartWrapper>
        )}
      </div>

      {/* Charts Row 3 - Arrears by Lines and Arrears by Value */}
      <div className="grid grid-cols-2 gap-4">
        <ChartWrapper
          title="ARREARS by LINES GRAPH"
          data={arrearsByLinesData}
          exportFilename="arrears-by-lines"
        >
          <ResponsiveContainer width="100%" height={220}>
            <LineChart
              data={arrearsByLinesData}
              onClick={(chartData) => handleChartClick(chartData, 'arrears')}
              style={{ cursor: 'pointer' }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
              <Line
                type="monotone"
                dataKey="lines"
                stroke="#ef4444"
                strokeWidth={2}
                dot={{ fill: '#ef4444', r: 4 }}
                name="PO Lines"
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartWrapper>

        <ChartWrapper
          // CR-6287: static label mirroring the sibling "ARREARS by LINES
          // GRAPH". The old title interpolated currency.currentCurrency,
          // which renders "[object Object]" whenever the resolved currency
          // is object-shaped rather than a plain code string.
          title="ARREARS by VALUES GRAPH"
          data={arrearsByValueData}
          exportFilename="arrears-by-value"
        >
          <ResponsiveContainer width="100%" height={220}>
            <LineChart
              data={arrearsByValueData}
              onClick={(chartData) => handleChartClick(chartData, 'arrears')}
              style={{ cursor: 'pointer' }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis
                tick={{ fontSize: 11 }}
                tickFormatter={axisAmount}
              />
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} formatter={(value: number) => formatCurrency(value)} />
              <Line
                type="monotone"
                dataKey="value"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={{ fill: '#3b82f6', r: 4 }}
                name="Value"
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartWrapper>
      </div>
    </div>
  )
}
